import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { getPromoById } from '~/services/PromoService';

export const promoCodeNameQueryKeys = {
  all: ['promo-code-names'] as const,
  detail: (promoId: string) => [...promoCodeNameQueryKeys.all, promoId] as const,
};

/** Resolves the display names for a bounded set of promo IDs without changing their ID-based links. */
export function useGetPromoCodeNames(promoIds: Array<string>) {
  const uniquePromoIds = useMemo(() => Array.from(new Set(promoIds.filter(Boolean))).sort(), [promoIds]);
  const queries = useQueries({
    queries: uniquePromoIds.map((promoId) => ({
      queryKey: promoCodeNameQueryKeys.detail(promoId),
      queryFn: ({ signal }: { signal?: AbortSignal }) => getPromoById({ idPromo: promoId, signal }),
      enabled: true,
      staleTime: 5 * 60 * 1000,
    })),
  });

  return useMemo(() => {
    const promoCodeNames = new Map<string, string>();

    uniquePromoIds.forEach((promoId, index) => {
      const result = queries[index]?.data;
      if (result?.status === 200 && result.result.code_name) {
        promoCodeNames.set(promoId, result.result.code_name);
      }
    });

    return promoCodeNames;
  }, [queries, uniquePromoIds]);
}
